import { useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, KeyValueList, Spinner, StatusPill } from '../../../ui'
import { CaseBundle, caseStatusTone, deadlineTone, describeDeadline, describeMatchMethod, formatDate, formatDateTime, humanise, sendKindLabel } from './shared'
import styles from './styles.module.scss'

// Switching between replies on the same claim should not refetch the case.
const bundleCache = new Map<string, CaseBundle>()

export const invalidateCaseBundle = (claimRef?: string | null) => {
    if (claimRef) bundleCache.delete(claimRef)
    else bundleCache.clear()
}

/**
 * Comms management: who this email is about. The client, the claim and where
 * the case stands. Which of our letters it answers is a separate concern and
 * lives in LetterLinkCard, because a claim accrues several letters of
 * different types over its life.
 */
export const CaseSummaryCard = ({
    claimRef,
    caseId,
    matchMethod,
    busy,
    onOpenCase,
    onShowAllEmails,
    onUnlink,
}: {
    claimRef: string
    caseId?: string | null
    matchMethod: string | null
    busy: boolean
    onOpenCase: () => void
    onShowAllEmails: () => void
    onUnlink: () => void
}) => {
    const [bundle, setBundle] = useState<CaseBundle | null>(bundleCache.get(claimRef) ?? null)
    const [loading, setLoading] = useState(!bundleCache.has(claimRef))
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const cached = bundleCache.get(claimRef)
        if (cached) {
            setBundle(cached)
            setLoading(false)
            return
        }
        setLoading(true)
        setError(null)
        api({ url: 'comms/inbox/case', data: { claimRef } })
            .then((resp) => {
                if (cancelled) return
                if (resp.data?.success) {
                    const b: CaseBundle = { claimRef, cases: resp.data.cases || [], identity: resp.data.identity || null, sends: resp.data.sends || [], replies: resp.data.replies || [] }
                    bundleCache.set(claimRef, b)
                    setBundle(b)
                } else setError(resp.data?.message || 'Could not load the case.')
            })
            .catch((err: any) => !cancelled && setError(err?.response?.data?.message || 'Could not load the case.'))
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
    }, [claimRef])

    const identity = bundle?.identity ?? null
    const commsCase = (caseId && bundle?.cases.find((c) => c.id === caseId)) || bundle?.cases[0] || null
    const closed = Boolean(commsCase?.closedAt)
    const otherReplies = bundle ? bundle.replies.length : 0
    const sends = bundle?.sends ?? []
    const lenderName = identity?.lenderName || sends[0]?.lenderName || commsCase?.lenderNormalisedName || null
    const lettersByKind = sends.reduce<Record<string, number>>((acc, s) => {
        const k = s.sendKind || 'initial_loc'
        acc[k] = (acc[k] || 0) + 1
        return acc
    }, {})
    const lettersSummary = sends.length ? Object.entries(lettersByKind).map(([k, n]) => `${n} ${sendKindLabel(k).toLowerCase()}${n === 1 ? '' : 's'}`).join(', ') : 'None sent yet'

    return (
        <Card
            padding="sm"
            title="Client and claim"
            subtitle={describeMatchMethod(matchMethod) ?? undefined}
            actions={
                <div className={styles.actionRow}>
                    <Button size="sm" variant="primary" onClick={onOpenCase} title="Full timeline of everything sent and received on this claim">
                        Case timeline
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onShowAllEmails} title="Filter the inbox to every email on this claim">
                        All emails{otherReplies ? ` (${otherReplies})` : ''}
                    </Button>
                    <Button size="sm" variant="link" onClick={onUnlink} disabled={busy} title="This email is not about this client or claim">
                        Wrong claim
                    </Button>
                </div>
            }
        >
            {loading ? (
                <Spinner block text="Loading client and claim…" />
            ) : error ? (
                <Banner tone="bad">{error}</Banner>
            ) : (
                <div className={styles.caseGrid}>
                    <div>
                        <div className={styles.caseColTitle}>Client</div>
                        {identity ? (
                            <KeyValueList
                                stacked
                                items={[
                                    { label: 'Name', value: identity.clientName },
                                    { label: 'Postcode', value: identity.clientPostcode },
                                    { label: 'Email', value: identity.clientEmail, mono: true },
                                    { label: 'Agreement no.', value: identity.agreementNumber, mono: true },
                                    { label: 'Vehicle', value: [identity.vehicle, identity.vehicleRegistration].filter(Boolean).join(' · ') || null },
                                ]}
                            />
                        ) : (
                            <Banner tone="warn">We do not hold a client snapshot for this claim yet, so the lender's details cannot be checked against our record.</Banner>
                        )}
                    </div>
                    <div>
                        <div className={styles.caseColTitle}>Claim</div>
                        <KeyValueList
                            stacked
                            items={[
                                { label: 'Claim ref', value: claimRef, mono: true },
                                { label: 'Lender', value: lenderName },
                                { label: 'Introducer', value: identity?.introducer ?? null },
                                { label: 'Letters we sent', value: lettersSummary },
                                { label: 'Emails received', value: otherReplies ? String(otherReplies) : null },
                            ]}
                        />
                    </div>
                    <div>
                        <div className={styles.caseColTitle}>Where the case stands</div>
                        {commsCase ? (
                            <KeyValueList
                                stacked
                                items={[
                                    {
                                        label: 'Status',
                                        value: (
                                            <StatusPill size="sm" tone={caseStatusTone(commsCase.status)} dot title={commsCase.closedReason || undefined}>
                                                {humanise(commsCase.status)}
                                            </StatusPill>
                                        ),
                                    },
                                    { label: 'Opened', value: formatDate(commsCase.openedAt) },
                                    {
                                        label: 'Acknowledgement due',
                                        value: commsCase.ackDeadline ? (
                                            <StatusPill size="sm" tone={deadlineTone(commsCase.ackDeadline, closed)}>
                                                {formatDate(commsCase.ackDeadline)}
                                                {!closed ? ` · ${describeDeadline(commsCase.ackDeadline)}` : ''}
                                            </StatusPill>
                                        ) : null,
                                    },
                                    {
                                        label: 'Final response due',
                                        value: commsCase.substantiveDeadline ? (
                                            <StatusPill size="sm" tone={deadlineTone(commsCase.substantiveDeadline, closed)}>
                                                {formatDate(commsCase.substantiveDeadline)}
                                                {!closed ? ` · ${describeDeadline(commsCase.substantiveDeadline)}` : ''}
                                            </StatusPill>
                                        ) : null,
                                    },
                                    { label: 'Last activity', value: formatDateTime(commsCase.lastActivityAt) },
                                    { label: "Lender's refs", value: commsCase.lenderRefs.length ? commsCase.lenderRefs.join(', ') : null, mono: true },
                                    { label: 'Closed', value: closed ? `${formatDate(commsCase.closedAt)}${commsCase.closedReason ? ` — ${humanise(commsCase.closedReason)}` : ''}` : null },
                                ]}
                            />
                        ) : (
                            <span className={styles.mutedInline}>No comms case has been opened for this claim yet.</span>
                        )}
                    </div>
                </div>
            )}
        </Card>
    )
}
